import { useCallback, useMemo } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { useCurrentUserId } from '#src/app/shared/hooks/session';
import type { ShareActionsOptions, ShareTrackingDataOverride } from '#src/app/shared/hooks/share';

/** The tracking data every share from the page carries, whatever the channel. */
function useShareTrackingData() {
	const userId = useCurrentUserId();
	const sourceLocation = useQueryParamValue('source_location');
	const {
		data: {
			petition: { id, user, photo, bestShareVisual },
		},
	} = usePsfPromoteOrSharePageData();

	return useMemo(
		() => ({
			context: 'psf_promote_or_share',
			view_context: 'psf_promote_or_share',
			source_location: sourceLocation,
			petition_id: `petition_${id}`,
			own_petition: userId === user.id,
			showImage: !!photo,
			recruiter_by_id: userId || undefined,
			is_share_asset: !!bestShareVisual?.url,
		}),
		[sourceLocation, id, userId, user.id, photo, bestShareVisual?.url],
	);
}

export function useGetShareTrackingData(): ShareActionsOptions['getTrackingData'] {
	const commonTrackingData = useShareTrackingData();
	return useCallback(() => commonTrackingData, [commonTrackingData]);
}

export type FacebookPostSheetCopyTrigger = 'facebook_tap' | 'post_cta' | 'group_card' | 'group_cta';

/**
 * EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment.
 * The Facebook post sheet's funnel events (CHANGE-99696), each carrying the variation that rendered
 * the sheet. Amplitude-only: see `TRACKING_EVENTS.md` for the proto opt-out.
 */
export function useFacebookPostSheetTracking() {
	const track = useTracking();
	const {
		data: {
			psfPromoteOrShareFacebookPostSheetExperiment: { variation },
		},
	} = usePsfPromoteOrSharePageData();

	return useMemo(() => {
		const sheetTrackingData = { facebook_post_sheet_variation: variation };
		return {
			trackViewed: () => {
				void track('combo_psf_share_slide_up_facebook_post_sheet_viewed', sheetTrackingData);
			},
			trackCopied: (trigger: FacebookPostSheetCopyTrigger, copied: boolean) => {
				void track('combo_psf_share_slide_up_facebook_post_sheet_copied', {
					...sheetTrackingData,
					facebook_post_sheet_copy_trigger: trigger,
					facebook_post_sheet_copy_result: copied ? 'success' : 'failure',
				});
			},
			trackDismissed: () => {
				void track('combo_psf_share_slide_up_facebook_post_sheet_dismissed', sheetTrackingData);
			},
			trackGroupCardViewed: (groupUrl: string) => {
				void track('combo_psf_share_slide_up_facebook_post_sheet_group_card_viewed', {
					...sheetTrackingData,
					recommendation_group_url: groupUrl,
				});
			},
			trackGroupCardTapped: (groupUrl: string) => {
				void track('combo_psf_share_slide_up_facebook_post_sheet_group_card_tapped', {
					...sheetTrackingData,
					recommendation_group_url: groupUrl,
				});
			},
		};
	}, [track, variation]);
}

// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
/** Why a QR view ended. See `qr_view_close_reason` in `@change-corgi/tracking-properties`. */
export type QrViewCloseReason = NonNullable<TrackingProperties['qr_view_close_reason']>;

export type QrCodeTracking = Readonly<{
	/** The "QR code" tile tap. Takes the tile's placement, as the channel tiles do. */
	trackTileTapped: (placement: ShareTrackingDataOverride) => void;
	trackViewed: () => void;
	trackClosed: (reason: QrViewCloseReason, dwellMs: number) => void;
	/** The Save image tap, with the share id the QR code carries. */
	trackSaved: (shareId: string | null) => void;
}>;

function getGeneratedShareIdData(shareId: string | null) {
	return shareId != null ? { generated_share_id: shareId, generated_share_id_source: 'qr_code_server' as const } : {};
}

/**
 * EXPERIMENT: psf_promote_or_share_qr_code_experiment.
 * The share slide-up's QR code events (CHANGE-99266). Each carries the page's share tracking data, so
 * `context` stays `psf_promote_or_share`. The tile tap is the QR channel's share: it fires
 * `share_petition_intent` and `share_petition` at the tap, as the Facebook tile does, so the arms
 * compare on `share_petition` at the same step. Save fires its own `psf_qr_save`, not a second
 * `share_petition`.
 *
 * `shareId` is the screen code's server-minted share id, or `null` while its fetch is pending.
 */
export function useQrCodeTracking(shareId: string | null): QrCodeTracking {
	const track = useTracking();
	const trackingData = useShareTrackingData();

	return useMemo(
		() => ({
			trackTileTapped: (placement) => {
				const tileTrackingData = { location: 'qr_code', ...trackingData, ...placement };
				void track('share_petition_intent', tileTrackingData);
				void track('share_petition', { ...tileTrackingData, ...getGeneratedShareIdData(shareId) });
			},
			trackViewed: () => {
				void track('psf_qr_view', trackingData);
			},
			trackClosed: (reason, dwellMs) => {
				void track('psf_qr_close', { ...trackingData, dwell_ms: dwellMs, qr_view_close_reason: reason });
			},
			trackSaved: (savedShareId) => {
				void track('psf_qr_save', {
					location: 'qr_code_download',
					...trackingData,
					...getGeneratedShareIdData(savedShareId),
				});
			},
		}),
		[track, trackingData, shareId],
	);
}
